import { useEffect, useRef, useState } from "react";
import { Loader2, X } from "lucide-react";
import { filterNomeInput, isNomeValido } from "@/lib/nomePessoa";
import {
  buildAtendimentoIdentificacao,
  filterAtendimentoNumeroInput,
  getAtendimentoLabels,
  limiteAtendimentoNumero,
  normalizeAtendimentoNome,
  normalizeAtendimentoNumero,
  parseAtendimentoIdentificacao,
  type ModoAtendimento,
} from "@/lib/atendimentoMode";
import { findComandaAbertaPorIdentificacao, type ComandaAberta } from "@caixa/lib/comandasApi";

const IDENTIFICACAO_LOOKUP_DEBOUNCE_MS = 350;

type Props = {
  open: boolean;
  modoAtendimento: ModoAtendimento;
  mesaAtual: string;
  comandaId: number;
  onClose: () => void;
  onConfirm: (mesa: string) => Promise<void>;
  loading?: boolean;
};

function normalizeIdentificacao(value: string): string {
  return value.trim();
}

export function AlterarMesaModal({
  open,
  modoAtendimento,
  mesaAtual,
  comandaId,
  onClose,
  onConfirm,
  loading = false,
}: Props) {
  const labels = getAtendimentoLabels(modoAtendimento);
  const [nome, setNome] = useState("");
  const [numero, setNumero] = useState("");
  const [mesa, setMesa] = useState("");
  const [checkingIdentificacao, setCheckingIdentificacao] = useState(false);
  const [existingComanda, setExistingComanda] = useState<ComandaAberta | null>(null);
  const lookupSeqRef = useRef(0);

  const identificacaoAtual = normalizeIdentificacao(mesaAtual);

  useEffect(() => {
    if (open) {
      const parsed = parseAtendimentoIdentificacao(mesaAtual, modoAtendimento);
      setNome(parsed.nome);
      setNumero(parsed.numero);
      setMesa(parsed.mesa ?? "");
      setCheckingIdentificacao(false);
      setExistingComanda(null);
      lookupSeqRef.current += 1;
    }
  }, [open, mesaAtual]);

  const identificacaoNormalizada = buildAtendimentoIdentificacao(modoAtendimento, nome, numero, mesa);
  const nomeNormalizado = normalizeAtendimentoNome(nome);
  const numeroNormalizado = normalizeAtendimentoNumero(numero, modoAtendimento);
  const nomeValido = !nomeNormalizado || isNomeValido(nomeNormalizado);
  const identificacaoValida = nomeValido && Boolean(nomeNormalizado || numeroNormalizado || (modoAtendimento === "ambos" && normalizeAtendimentoNumero(mesa, "mesa")));
  const isSameIdentificacao = Boolean(identificacaoNormalizada) && identificacaoNormalizada === identificacaoAtual;

  useEffect(() => {
    if (!open) return;

    if (!identificacaoNormalizada || isSameIdentificacao) {
      lookupSeqRef.current += 1;
      setCheckingIdentificacao(false);
      setExistingComanda(null);
      return;
    }

    const seq = ++lookupSeqRef.current;
    const controller = new AbortController();
    setCheckingIdentificacao(true);

    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const found = await findComandaAbertaPorIdentificacao(identificacaoNormalizada, {
            signal: controller.signal,
          });
          if (lookupSeqRef.current !== seq) return;
          if (found && found.id === comandaId) {
            setExistingComanda(null);
          } else {
            setExistingComanda(found);
          }
        } catch (e) {
          if (e instanceof Error && e.name === "AbortError") return;
          if (lookupSeqRef.current !== seq) return;
          setExistingComanda(null);
        } finally {
          if (lookupSeqRef.current === seq) {
            setCheckingIdentificacao(false);
          }
        }
      })();
    }, IDENTIFICACAO_LOOKUP_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [open, identificacaoNormalizada, isSameIdentificacao, comandaId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identificacaoValida || checkingIdentificacao || loading || isSameIdentificacao) return;
    await onConfirm(identificacaoNormalizada);
  };

  if (!open) return null;

  const canSubmit = identificacaoValida && !loading && !checkingIdentificacao && !isSameIdentificacao;

  return (
    <div className="caixa-overlay caixa-overlay--60">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="alterar-mesa-title"
        className="caixa-modal"
      >
        <div className="caixa-modal-head">
          <h2 id="alterar-mesa-title" className="caixa-modal-title">
            Alterar {labels.singular}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="caixa-modal-close"
            aria-label="Fechar"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={(e) => void handleSubmit(e)}>
          <div className="caixa-field">
            <div className="flex items-center justify-between gap-3">
              <label htmlFor="alterar-mesa-nome">Nome</label>
              <span className="text-xs text-zinc-400">{nome.length}/10</span>
            </div>
            <div className="relative">
              <input
                id="alterar-mesa-nome"
                type="text"
                autoComplete="off"
                value={nome}
                maxLength={10}
                onChange={(e) => setNome(filterNomeInput(e.target.value).slice(0, 10))}
                onBlur={() => setNome((value) => normalizeAtendimentoNome(value))}
                placeholder="Ex: João"
                className="caixa-input pr-10"
                autoFocus
                disabled={loading}
              />
              {checkingIdentificacao ? (
                <Loader2
                  className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-zinc-400"
                  aria-label="Verificando identificação"
                />
              ) : null}
            </div>
          </div>
          <div className="caixa-field">
            <label htmlFor="alterar-mesa-numero">{labels.numeroLabel}</label>
            <input
              id="alterar-mesa-numero"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              value={numero}
              maxLength={limiteAtendimentoNumero(modoAtendimento)}
              onChange={(e) => setNumero(filterAtendimentoNumeroInput(e.target.value, modoAtendimento))}
              onBlur={() => setNumero((value) => normalizeAtendimentoNumero(value, modoAtendimento))}
              placeholder="Ex: 5"
              className="caixa-input"
              disabled={loading}
            />
          </div>
          {isSameIdentificacao ? (
            <p className="caixa-alert">Informe uma identificação diferente da atual.</p>
          ) : null}
          {modoAtendimento === "ambos" ? <div className="caixa-field">
            <label htmlFor="alterar-mesa-mesa">Nº Mesa</label>
            <input id="alterar-mesa-mesa" className="caixa-input" type="text" inputMode="numeric" maxLength={3} value={mesa} disabled={loading}
              onChange={(e) => setMesa(filterAtendimentoNumeroInput(e.target.value, "mesa"))}
              onBlur={() => setMesa((value) => normalizeAtendimentoNumero(value, "mesa"))} />
          </div> : null}
          {modoAtendimento !== "ambos" && identificacaoNormalizada && !checkingIdentificacao && !isSameIdentificacao && existingComanda ? (
            <p className="caixa-alert">{labels.jaExiste} Ao salvar, os itens serão adicionados a ela.</p>
          ) : null}
          <div className="caixa-modal-footer">
            <button type="button" onClick={onClose} className="caixa-btn-outline" disabled={loading}>
              Cancelar
            </button>
            <button type="submit" disabled={!canSubmit} className="caixa-btn-primary">
              {loading ? "Alterando..." : `Alterar ${labels.singular}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
